/**
 * An indicator lamp.
 *
 * This is the one piece of material the whole design rests on, so it is a
 * component rather than a CSS class every view reimplements. A lens has a
 * bright core, a dark rim that comes from its own gradient, and one neutral
 * drop shadow. It does NOT have a coloured glow: a zero-offset chromatic halo
 * on a dark ground is the default look of every generated dashboard on the
 * internet, and the first version of this file shipped one at five call sites
 * before the detector caught it. Hardware reads as hardware because of the
 * highlight and the bezel, not because of spill.
 *
 * `off` is a real state, not an absent one. A service that has never reported
 * is a dark lens in its bezel — you can see the lamp, and you can see it is
 * not lit. Rendering nothing there would say something different.
 *
 * Colour is never the only channel: every call site pairs a lens with a label,
 * a count or a name. The lens is what you notice; the text is what you read.
 */

import styles from "./Lens.module.css";

/**
 * @param {object} props
 * @param {"ok"|"warn"|"danger"|"off"|"tone"} [props.tone] What the lamp is
 *   saying. `tone` reads the caller's own `--tone` for a categorical colour.
 * @param {"sm"|"md"} [props.size]
 * @param {string} [props.label] An accessible name, when no adjacent text
 *   already provides one. Without it the lens is decorative and hidden.
 */
export function Lens({ tone = "ok", size = "md", label, className = "", ...rest }) {
  return (
    <span
      className={`${styles.lens} ${styles[size]} ${className}`}
      data-tone={tone}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
      {...rest}
    />
  );
}
